import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, ChevronLeft, Info, CreditCard, X } from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { toast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import PaymentGatewaySelection from "@/components/payment/PaymentGatewaySelection";
import { SubscriptionPlan } from "@/services/paymentService";
import { subscriptionService } from "@/services/subscriptionService";

const SubscriptionPlans = () => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentPlanForPayment, setCurrentPlanForPayment] =
    useState<SubscriptionPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string>("PAYG");
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();

  // Fetch user's current subscription on component mount
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!user?.id) return;

      try {
        const subscription =
          await subscriptionService.getUserActiveSubscription(user.id);
        if (subscription && subscription.status === "active") {
          setCurrentPlanId(subscription.plan_id);
        } else {
          setCurrentPlanId("PAYG");
        }
      } catch (error) {
        console.error("Error fetching current subscription:", error);
        setCurrentPlanId("PAYG");
      }
    };

    fetchCurrentPlan();
  }, [user?.id]);

  const plans = [
    {
      id: "PAYG",
      name: "PAYG",
      headerTag: "Pay-As-You-Go",
      tagbackground: "#A8E6CF",
      price: {
        main: "0",
        decimals: null,
        period: "Pay for what you use",
      },
      description:
        "Pay-as-you-go access — purchase credits as needed for premium actions",
      featuresHeader: "PAYG features:",
      features: [
        {
          name: "Basic environmental data access",
          included: true,
          tooltip: "Access to global environmental datasets.",
        },
        {
          name: "On-demand reports via credits",
          included: true,
          tooltip: "Generate reports by spending credits.",
        },
        {
          name: "Bookmarks and projects (limited by credits)",
          included: true,
          tooltip:
            "Create projects and bookmarks; additional invites cost credits.",
        },
      ],
      cta: "Current Plan",
      disabled: true,
      paymentData: {
        id: "PAYG",
        name: "PAYG",
        price: 0,
        currency: "ZAR",
        interval: "month" as const,
        features: ["Pay-as-you-go credit billing"],
      },
    },
    {
      id: "standard",
      name: "Standard",
      headerTag: "Best for individuals, small teams",
      tagbackground: "#4285F4",
      price: {
        main: "128",
        decimals: "68",
        period: "Billed monthly",
      },
      description: "Enhanced monitoring capabilities for professionals",
      featuresHeader: "Everything in Free, plus:",
      features: [
        {
          name: "Comprehensive regional insights",
          included: true,
          tooltip: "In-depth insights for all available regions.",
        },
        {
          name: "High resolution imagery",
          included: true,
          tooltip: "Access to high-resolution satellite imagery.",
        },
        {
          name: "5 simultaneous projects limit",
          included: true,
          tooltip:
            "Create and manage up to 5 conservation projects simultaneously.",
        },
        {
          name: "10 reports generation per month",
          included: true,
          tooltip: "Generate up to 10 environmental reports each month.",
        },
        {
          name: "10 regions bookmark limit",
          included: true,
          tooltip: "Save up to 10 regions to your bookmarks for quick access.",
        },
        {
          name: "5-Year environmental predictions",
          included: true,
          tooltip: "Utilize our AI for 5-year environmental forecasts.",
        },
        {
          name: "Advanced data analysis tools",
          included: true,
          tooltip: "Use advanced tools for data comparison and analysis.",
        },
        {
          name: "Regional Insights",
          included: true,
          tooltip:
            "Enriched environmental data per region: air quality, climate trends, carbon analytics, and extreme weather events.",
        },
        {
          name: "WhatsApp carbon tracking",
          included: true,
          tooltip: "Track your carbon footprint via WhatsApp integration.",
        },
        {
          name: "AI Summary",
          included: true,
          tooltip: "Generate AI-powered summaries of your environmental data.",
        },
      ],
      cta: "Subscribe",
      popular: true,
      paymentData: {
        id: "standard",
        name: "Standard",
        price: 128.68,
        currency: "ZAR",
        interval: "month" as const,
        features: [
          "Comprehensive regional insights",
          "High resolution imagery",
          "Unlimited PDF exports",
        ],
      },
    },
    {
      id: "professional",
      name: "Professional",
      headerTag: "Best for teams",
      tagbackground: "#FFB300",
      price: {
        main: "96",
        decimals: "42",
        period: "Save 28% with annual billing - R1,157.97/year",
      },
      description: "Complete solution for organizations and researchers",
      featuresHeader: "Everything in Standard, plus:",
      features: [
        {
          name: "Global insights with custom regions",
          included: true,
          tooltip: "Define and monitor your own custom regions.",
        },
        {
          name: "Highest resolution imagery",
          included: true,
          tooltip:
            "Get access to the highest available satellite imagery resolution.",
        },
        {
          name: "10 simultaneous projects limit",
          included: true,
          tooltip:
            "Create and manage up to 10 conservation projects simultaneously.",
        },
        {
          name: "Unlimited reports generation per month",
          included: true,
          tooltip:
            "Generate as many environmental reports as you need each month.",
        },
        {
          name: "20 regions bookmark limit",
          included: true,
          tooltip: "Save up to 20 regions to your bookmarks for quick access.",
        },
        {
          name: "10-Year environmental predictions",
          included: true,
          tooltip: "Extended 10-year environmental forecasting.",
        },
        {
          name: "20-year historical data access",
          included: true,
          tooltip: "Access our extensive 20-year historical data archive.",
        },
        {
          name: "Customizable report templates",
          included: true,
          tooltip: "Customize PDF reports with your own branding and layout.",
        },
        {
          name: "Priority 24/7 support",
          included: true,
          tooltip: "Get dedicated, round-the-clock support from our team.",
        },
        {
          name: "Regional Insights",
          included: true,
          tooltip:
            "Enriched environmental data per region: air quality, climate trends, carbon analytics, and extreme weather events.",
        },
        {
          name: "WhatsApp carbon tracking",
          included: true,
          tooltip: "Track your carbon footprint via WhatsApp integration.",
        },
        {
          name: "AI Summary",
          included: true,
          tooltip: "Generate AI-powered summaries of your environmental data.",
        },
      ],
      cta: "Subscribe",
      popular: false,
      paymentData: {
        id: "professional",
        name: "Professional",
        price: 1157.97,
        currency: "ZAR",
        interval: "year" as const,
        features: [
          "Global insights with custom regions",
          "Highest resolution imagery",
          "10-year predictions",
        ],
      },
    },
  ];

  const { currency, formatPrice } = useCurrency();

  // ZAR monthly price per plan (used for display conversion)
  const PLAN_ZAR_MONTHLY: Record<string, number> = {
    PAYG: 0,
    standard: 128.68,
    professional: 96.42,
  };

  const getCurrentPlanId = () => {
    // Return the state value that's fetched from the database
    return currentPlanId;
  };

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to subscribe to a plan",
        variant: "destructive",
      });
      return;
    }

    const planData = plans.find((p) => p.id === planId);
    if (!planData || !planData.paymentData) {
      toast({
        title: "Plan Not Found",
        description: "The selected plan could not be found.",
        variant: "destructive",
      });
      return;
    }

    // Handle PAYG plan - directly update account tier
    if (planId === "PAYG") {
      try {
        const success = await subscriptionService.updateUserAccountTier(
          user.id,
          "PAYG",
        );
        if (success) {
          setCurrentPlanId("PAYG");
          toast({
            title: "Plan Updated",
            description: "You are now on the PAYG plan.",
            variant: "default",
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to update your plan. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error updating to free plan:", error);
        toast({
          title: "Error",
          description: "Failed to update your plan. Please try again.",
          variant: "destructive",
        });
      }
      return;
    }

    // For paid plans, open payment gateway selection dialog
    setSelectedPlan(planId);
    setCurrentPlanForPayment(planData.paymentData);
    setShowPaymentDialog(true);
    // Reset selection state after dialog opens
    setTimeout(() => setSelectedPlan(null), 1000);
  };

  const handlePaymentInitiated = (sessionId: string, redirectUrl?: string) => {
    setShowPaymentDialog(false);
    setCurrentPlanForPayment(null);

    // Store session info and plan details for return handling
    if (typeof window !== "undefined") {
      localStorage.setItem("pendingPaymentSession", sessionId);
      if (currentPlanForPayment) {
        localStorage.setItem("pendingPlanId", currentPlanForPayment.id);
        localStorage.setItem("pendingPlanName", currentPlanForPayment.name);
        localStorage.setItem(
          "pendingPlanAmount",
          currentPlanForPayment.price.toString(),
        );
        localStorage.setItem(
          "pendingPlanCurrency",
          currentPlanForPayment.currency,
        );
      }
    }

    toast({
      title: "Payment Initiated",
      description: "Redirecting to payment provider...",
    });

    // Redirect will be handled by the payment service
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex flex-col relative">
        {/* Background Image */}
        <div
          className="absolute inset-0 opacity-[.5] dark:opacity-[.2] pointer-events-none"
          style={{
            backgroundImage: "url(/map.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Header */}
        <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 relative">
          {/* Rest of header content */}
          <div className="container flex h-16 items-center">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mr-4 gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            {/* Beta and Version Badge */}
            <div className="flex items-center gap-2 mr-4">
              <span className="text-[11px] font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                Beta
              </span>
              <span className="text-[11px] text-muted-foreground">v1.1</span>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold">Subscription Plans</h1>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 container py-12 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Page Intro */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Upgrade Your Environmental Monitoring
              </h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                Choose the plan that best suits your needs and get access to
                advanced environmental data, predictions, and analysis tools.
              </p>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {plans.map((plan) => {
                const isCurrentPlan = plan.id === getCurrentPlanId();
                return (
                  <Card
                    key={plan.id}
                    className={cn(
                      "flex flex-col h-full rounded-2xl transition-all duration-200",
                      plan.popular && !isCurrentPlan
                        ? "border-2 border-primary shadow-2xl"
                        : "border",
                      isCurrentPlan
                        ? "bg-gradient-to-br from-primary/20 to-primary/10 dark:from-primary/30 dark:to-primary/15 border-2 border-primary/60 shadow-xl ring-2 ring-primary/20"
                        : "",
                    )}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h3
                          className={cn(
                            "text-lg font-semibold text-left",
                            isCurrentPlan ? "text-primary font-bold" : "",
                          )}
                        >
                          {plan.name}
                        </h3>
                        {plan.popular && !isCurrentPlan && (
                          <Badge
                            variant="secondary"
                            className="bg-primary text-primary-foreground"
                          >
                            Most Popular
                          </Badge>
                        )}
                        {isCurrentPlan && (
                          <Badge className="bg-primary text-primary-foreground font-medium">
                            Current Plan
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span
                          className={cn(
                            "text-3xl font-bold",
                            isCurrentPlan ? "text-primary" : "",
                          )}
                        >
                          {PLAN_ZAR_MONTHLY[plan.id] > 0
                            ? formatPrice(PLAN_ZAR_MONTHLY[plan.id])
                            : "Free"}
                        </span>
                      </div>
                      <p
                        className={cn(
                          "text-sm text-muted-foreground text-left",
                          isCurrentPlan ? "text-primary/70" : "",
                        )}
                      >
                        {plan.id === "professional"
                          ? `Save 28% with annual billing — ${formatPrice(1157.97)}/year`
                          : plan.price.period}
                      </p>
                      {currency === "USD" && PLAN_ZAR_MONTHLY[plan.id] > 0 && (
                        <p className="text-xs text-muted-foreground/60 mt-1">
                          * indicative, ~R18.50 per $1
                        </p>
                      )}
                    </CardHeader>

                    <CardContent className="flex-1 p-6 pt-0">
                      <div className="space-y-4">
                        <Button
                          className={cn(
                            "w-full text-lg py-6",
                            isCurrentPlan
                              ? "bg-primary/20 hover:bg-primary/30 text-primary border-2 border-primary/40 font-semibold"
                              : "",
                          )}
                          variant={isCurrentPlan ? "outline" : "default"}
                          disabled={
                            plan.disabled || isCurrentPlan || isProcessing
                          }
                          onClick={() => handleSubscribe(plan.id)}
                        >
                          {isProcessing && selectedPlan === plan.id
                            ? "Processing..."
                            : isCurrentPlan
                              ? "Current Plan"
                              : plan.cta}
                        </Button>
                        {plan.contactSales && (
                          <Button
                            variant="link"
                            className="w-full justify-center"
                          >
                            Contact Sales
                          </Button>
                        )}
                        <div className="pt-4">
                          <p className="font-semibold mb-3 text-left">
                            {plan.featuresHeader}
                          </p>
                          <ul className="space-y-3">
                            {plan.features.map((feature, index) => (
                              <li
                                key={index}
                                className={`flex items-center gap-2 ${!feature.included ? "opacity-50" : ""}`}
                              >
                                {feature.included ? (
                                  <Check
                                    className={cn(
                                      "h-5 w-5 flex-shrink-0",
                                      isCurrentPlan
                                        ? "text-primary"
                                        : "text-green-500",
                                    )}
                                  />
                                ) : (
                                  <X className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                )}
                                <span
                                  className={`flex-1 text-left ${!feature.included ? "line-through text-muted-foreground" : ""}`}
                                >
                                  {feature.name}
                                </span>
                                {feature.tooltip && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Info className="h-4 w-4 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{feature.tooltip}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* FAQ Section */}
            <div className="mt-20">
              <h3 className="text-3xl font-bold mb-8 text-center">
                Frequently Asked Questions
              </h3>
              <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <h4 className="font-semibold text-lg">
                    How do I change my subscription?
                  </h4>
                  <p className="text-muted-foreground mt-1">
                    You can upgrade, downgrade, or cancel your subscription at
                    any time from your account settings.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg">
                    Do you offer refunds?
                  </h4>
                  <p className="text-muted-foreground mt-1">
                    Yes, we offer a 14-day money-back guarantee if you're not
                    satisfied with your subscription.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg">
                    Can I try the Pro features before subscribing?
                  </h4>
                  <p className="text-muted-foreground mt-1">
                    We occasionally offer trial periods for new users. Check our
                    promotions page or contact support for current offers.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg">
                    What payment methods do you accept?
                  </h4>
                  <p className="text-muted-foreground mt-1">
                    We accept all major credit cards via Paystack for secure
                    online payments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-6 text-center text-sm text-muted-foreground border-t">
          <p>
            &co open={showPaymentDialog}
            onOpenChange=
            {(open) => {
              setShowPaymentDialog(open);
              if (!open) {
                // Clear current plan when dialog is closed by user
                setTimeout(() => setCurrentPlanForPayment(null), 300);
              }
            }}
            include our{" "}
            <a
              href="/terms-and-privacy"
              className="underline hover:text-primary"
            >
              Terms of Service
            </a>
            .
          </p>
        </footer>

        {/* Payment Gateway Selection Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Complete Your Subscription
              </DialogTitle>
              <DialogDescription className="sr-only">
                Select your preferred payment method to complete your
                subscription
              </DialogDescription>
            </DialogHeader>
            {currentPlanForPayment && (
              <PaymentGatewaySelection
                plan={currentPlanForPayment}
                onPaymentInitiated={handlePaymentInitiated}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                onCloseDialog={() => setShowPaymentDialog(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default SubscriptionPlans;
